'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const subProducts = [
  {
    icon: 'FireIcon',
    title: 'Food Delivery',
    color: '#B45309',
    bgColor: 'rgba(180,83,9,0.08)',
    gradientFrom: '#92400E',
    gradientTo: '#B45309',
    href: '/products/food-delivery',
    tagline: 'Restaurant to doorstep',
    description:
      'End-to-end food delivery platform with restaurant management, menu builder, real-time order tracking, and smart delivery dispatch — all under your own brand.',
    features: [
      'Restaurant & Menu Management Portal',
      'Customer App with Live Order Tracking',
      'Smart Delivery Partner Dispatch',
      'ETA Predictions & Order Alerts',
      'Multi-payment: UPI, Card, Wallet & COD',
      'Promotions, Offers & Loyalty Programs',
    ],
  },
  {
    icon: 'ShoppingCartIcon',
    title: 'Grocery Delivery',
    color: '#15803D',
    bgColor: 'rgba(21,128,61,0.08)',
    gradientFrom: '#14532D',
    gradientTo: '#15803D',
    href: '/products/grocery',
    tagline: 'Slot-based store-to-door fulfilment',
    description:
      'Launch your own branded grocery delivery platform with real-time inventory management, slot-based delivery scheduling, and seamless last-mile fulfilment.',
    features: [
      'Real-time Inventory & Stock Management',
      'Slot-based Delivery Scheduling',
      'Customer App with Smart Search & Filters',
      'Last-mile Delivery Optimization',
      'Offers, Discounts & Subscription Plans',
      'Sales Analytics & Demand Forecasting',
    ],
  },
  {
    icon: 'Squares2X2Icon',
    title: 'Multi-Vendor Marketplace',
    color: '#BE185D',
    bgColor: 'rgba(190,24,93,0.08)',
    gradientFrom: '#831843',
    gradientTo: '#BE185D',
    href: '/products/multi-vendor',
    tagline: 'Commission-based multi-seller platform',
    description:
      'Build a full-scale marketplace where multiple sellers, restaurants, or service providers operate under your brand with centralized control, commission management, and automated payouts.',
    features: [
      'Vendor Onboarding & Profile Management',
      'Commission & Revenue Split Engine',
      'Individual Vendor Dashboards & Analytics',
      'Centralized Quality & Compliance Control',
      'Automated Payout & Settlement System',
      'Marketplace-wide Performance Analytics',
    ],
  },
  {
    icon: 'BuildingStorefrontIcon',
    title: 'Single Store',
    color: '#7C3AED',
    bgColor: 'rgba(124,58,237,0.08)',
    gradientFrom: '#4C1D95',
    gradientTo: '#7C3AED',
    href: '/products/single-store',
    tagline: 'Instant digital commerce for one store',
    description:
      'A powerful digital commerce solution for individual store owners to go online instantly — with their own branded app, order management, delivery operations, and payment integration.',
    features: [
      'Branded Store App for iOS & Android',
      'Product Catalog & Order Management',
      'Integrated Payments & Invoicing',
      'Own Delivery Fleet Management',
      'WhatsApp Order Notifications',
      'Store Performance Dashboard',
    ],
  },
  {
    icon: 'ArrowsRightLeftIcon',
    title: 'Delivery Integrations',
    color: '#0F766E',
    bgColor: 'rgba(15,118,110,0.08)',
    gradientFrom: '#134E4A',
    gradientTo: '#0F766E',
    href: '/products/delivery-integrations',
    tagline: 'Third-party delivery API aggregation',
    description:
      'Seamlessly connect your platform with third-party delivery networks, logistics APIs, and fulfilment partners to extend your delivery reach without building infrastructure from scratch.',
    features: [
      'Third-party Delivery API Integrations',
      'Multi-partner Live Tracking Aggregation',
      'Unified Order & Fulfilment Management',
      'Auto-routing to Best Available Partner',
      'Cross-platform Delivery Analytics',
      'SLA Monitoring & Partner Performance',
    ],
  },
];

const platformBenefits = [
  {
    icon: 'ShieldCheckIcon',
    title: 'One Reliable Infrastructure',
    desc: 'All five platforms share the same battle-tested backend — payments, tracking, notifications, and analytics — so every vertical benefits from shared reliability.',
  },
  {
    icon: 'ChartBarIcon',
    title: 'Unified Analytics Dashboard',
    desc: 'View performance across all your e-commerce verticals in one dashboard — orders, revenue, delivery SLAs, and customer retention side by side.',
  },
  {
    icon: 'BoltIcon',
    title: 'Faster Time to Market',
    desc: 'Launch one vertical or all five simultaneously. Each platform is pre-built and configurable — no custom development needed to go live.',
  },
  {
    icon: 'CurrencyRupeeIcon',
    title: 'Cost Efficient Bundling',
    desc: 'Running multiple delivery verticals under one Movio subscription is significantly more cost-effective than piecing together separate point solutions.',
  },
];

const stats = [
  { value: '5', label: 'Delivery Verticals', icon: 'Squares2X2Icon' },
  { value: '1', label: 'Unified Platform', icon: 'ShieldCheckIcon' },
  { value: 'Real-time', label: 'Order Tracking', icon: 'MapPinIcon' },
  { value: 'Multi-pay', label: 'UPI · Card · COD', icon: 'CreditCardIcon' },
];

export default function ECommerceContent() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (el) {
      const t = setTimeout(() => {
        el.querySelectorAll('.animate-on-scroll').forEach((item) => item.classList.add('is-visible'));
      }, 30);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pt-20">

      {/* ── HERO ── */}
      <div
        ref={heroRef}
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0C3B47 0%, #0E7490 55%, #0891B2 100%)' }}
      >
        <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
        <div className="orb absolute w-[600px] h-[600px] rounded-full opacity-15 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 65%)', top: '-200px', right: '-150px' }} />
        <div className="orb-2 absolute w-[400px] h-[400px] rounded-full opacity-10 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.6) 0%, transparent 70%)', bottom: '-80px', left: '-80px' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left */}
            <div>
              <Link href="/products" className="animate-on-scroll stagger-1 inline-flex items-center gap-2 text-white/60 text-sm font-medium mb-7 hover:text-white transition-colors">
                <Icon name="ArrowLeftIcon" size={14} />
                Back to Products
              </Link>

              <span className="animate-on-scroll stagger-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-5">
                <Icon name="ShoppingBagIcon" size={11} />
                E-Commerce Suite
              </span>

              <h1 className="animate-on-scroll stagger-2 font-display text-5xl md:text-6xl font-semibold text-white leading-tight tracking-tight mb-5 mt-2">
                E-Commerce & Retail —{' '}
                <span className="italic font-light" style={{ color: '#A5F3FC' }}>
                  Five Platforms,
                </span>
                <br />One Foundation.
              </h1>

              <p className="animate-on-scroll stagger-3 text-white/65 text-lg leading-relaxed mb-9 max-w-lg">
                Food Delivery, Grocery, Multi-Vendor Marketplace, Single Store, and Delivery Integrations — five proven commerce and delivery platforms built on one reliable, scalable infrastructure.
              </p>

              <div className="animate-on-scroll stagger-4 flex flex-wrap gap-4 mb-8">
                <Link href="/demo-booking" className="btn-primary inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-semibold">
                  Book a Free Demo
                  <Icon name="ArrowRightIcon" size={16} />
                </Link>
                <Link href="/contact" className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-medium text-white/75 border border-white/25 hover:border-white/50 hover:text-white transition-all">
                  <Icon name="PhoneIcon" size={15} />
                  +91 9600830488
                </Link>
              </div>

              <div className="animate-on-scroll stagger-5 flex flex-wrap gap-x-6 gap-y-2">
                {['Food Delivery', 'Grocery', 'Multi-Vendor', 'Single Store', 'Delivery Integrations'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 text-xs text-white/50 font-medium">
                    <Icon name="CheckCircleIcon" size={13} className="text-[#A5F3FC]" />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — 5 platform icons grid */}
            <div className="animate-on-scroll stagger-3">
              <div
                className="rounded-2xl p-6 border border-white/15"
                style={{ background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(14px)' }}
              >
                <p className="text-[11px] font-semibold text-white/40 uppercase tracking-widest mb-5">5 Platforms Inside This Suite</p>
                <div className="flex flex-col gap-3">
                  {subProducts.map((p) => (
                    <div
                      key={p.title}
                      className="flex items-center gap-4 p-3.5 rounded-xl bg-white/[0.06] border border-white/10 hover:bg-white/[0.12] transition-colors"
                    >
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: `linear-gradient(135deg, ${p.gradientFrom}, ${p.gradientTo})` }}
                      >
                        <Icon name={p.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-white">{p.title}</p>
                        <p className="text-[11px] text-white/50">{p.tagline}</p>
                      </div>
                      <Icon name="ChevronRightIcon" size={14} className="text-white/30" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── METRICS STRIP ── */}
      <div style={{ background: 'linear-gradient(135deg, #0C3B47 0%, #0E7490 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {stats.map((s, i) => (
              <div key={s.label} className={`animate-on-scroll stagger-${i + 1} flex items-center gap-4 px-6 py-7`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/15">
                  <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-white" />
                </div>
                <div>
                  <div className="font-display text-2xl font-semibold text-white leading-none mb-0.5">{s.value}</div>
                  <div className="text-xs text-white/60 font-medium">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 5 SUB-PRODUCTS DEEP DIVE ── */}
      <div className="py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(14,116,144,0.3)] bg-[rgba(14,116,144,0.08)] text-[11px] font-semibold tracking-widest text-[#0E7490] uppercase mb-4">
              Platform Suite
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Five platforms.{' '}
              <span className="italic font-light text-[#0E7490]">Every e-commerce vertical covered.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-2xl mx-auto">
              Each platform is purpose-built for its vertical — click to explore the full feature set.
            </p>
          </div>

          <div className="flex flex-col gap-8">
            {subProducts.map((product, idx) => (
              <div
                key={product.title}
                className={`animate-on-scroll stagger-${Math.min(idx + 2, 6)} bg-white rounded-2xl border border-[rgba(15,23,42,0.07)] shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300`}
              >
                <div className="grid lg:grid-cols-5">
                  {/* Left panel */}
                  <div
                    className="lg:col-span-2 p-8 flex flex-col justify-between"
                    style={{ background: `linear-gradient(135deg, ${product.gradientFrom} 0%, ${product.gradientTo} 100%)` }}
                  >
                    <div>
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 bg-white/20">
                        <Icon name={product.icon as Parameters<typeof Icon>[0]['name']} size={28} className="text-white" />
                      </div>
                      <h3 className="font-display text-2xl font-semibold text-white leading-tight mb-2">
                        {product.title}
                      </h3>
                      <p className="text-white/60 text-sm font-medium mb-4">{product.tagline}</p>
                      <p className="text-white/75 text-sm leading-relaxed">{product.description}</p>
                    </div>
                    <div className="mt-8">
                      <Link
                        href={product.href}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold bg-white transition-all duration-200 hover:bg-white/90 hover:-translate-y-0.5 shadow-md"
                        style={{ color: product.gradientTo }}
                      >
                        Explore {product.title}
                        <Icon name="ArrowRightIcon" size={15} />
                      </Link>
                    </div>
                  </div>

                  {/* Right panel — features */}
                  <div className="lg:col-span-3 p-8" style={{ background: product.bgColor }}>
                    <p
                      className="text-xs font-semibold uppercase tracking-widest mb-5"
                      style={{ color: product.color }}
                    >
                      Key Features
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {product.features.map((feature) => (
                        <div key={feature} className="flex items-start gap-3 p-3.5 rounded-xl bg-white border border-[rgba(15,23,42,0.06)]">
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ background: product.bgColor }}
                          >
                            <Icon name="CheckIcon" size={11} style={{ color: product.color }} />
                          </div>
                          <span className="text-sm text-[#475569] leading-snug font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── WHY E-Commerce & Retail ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(14,116,144,0.3)] bg-[rgba(14,116,144,0.08)] text-[11px] font-semibold tracking-widest text-[#0E7490] uppercase mb-4">
              Why This Suite
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              The reliability advantage of{' '}
              <span className="italic font-light text-[#0E7490]">a unified suite.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-xl mx-auto">
              Running five separate platforms independently creates fragmentation. E-Commerce & Retail bundles them under one proven, scalable infrastructure.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {platformBenefits.map((b, i) => (
              <div
                key={b.title}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group p-6 rounded-2xl border border-[rgba(14,116,144,0.1)] hover:border-[rgba(14,116,144,0.3)] hover:shadow-md transition-all duration-300`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                  style={{ background: 'rgba(14,116,144,0.1)' }}
                >
                  <Icon name={b.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0E7490]" />
                </div>
                <p className="font-semibold text-sm text-[#0F172A] mb-2">{b.title}</p>
                <p className="text-xs text-[#64748B] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="py-24 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="animate-on-scroll stagger-1 rounded-3xl p-12 md:p-16 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #0C3B47 0%, #0E7490 55%, #0891B2 100%)' }}
          >
            <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
            <div className="absolute top-0 right-0 w-72 h-72 pointer-events-none rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)', transform: 'translate(50px,-50px)' }} />

            <span className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-6">
              <span className="relative flex h-1.5 w-1.5">
                <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
              </span>
              Launch Your E-Commerce Stack
            </span>

            <h2 className="relative font-display text-4xl md:text-5xl font-semibold text-white tracking-tight mb-4">
              One platform suite,{' '}
              <span className="italic font-light text-[#A5F3FC]">every delivery vertical.</span>
            </h2>

            <p className="relative text-white/65 text-lg mb-5 max-w-xl mx-auto leading-relaxed">
              For demo or more information
            </p>

            <div className="relative flex flex-wrap justify-center gap-x-5 gap-y-2 mb-10 text-sm">
              <a href="tel:+919600830488" className="inline-flex items-center gap-2 text-white/70 font-medium hover:text-white transition-colors">
                <Icon name="PhoneIcon" size={14} />
                +91 9600830488
              </a>
              <span className="text-white/25 hidden sm:inline">|</span>
              <a href="mailto:contact@movio.in" className="inline-flex items-center gap-2 text-white/70 font-medium hover:text-white transition-colors">
                <Icon name="EnvelopeIcon" size={14} />
                contact@movio.in
              </a>
            </div>

            <div className="relative flex flex-wrap justify-center gap-4">
              <Link
                href="/demo-booking"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold bg-white text-[#0E7490] hover:bg-white/90 shadow-lg transition-all duration-200 hover:-translate-y-0.5"
              >
                Book Free Demo
                <Icon name="ArrowRightIcon" size={18} />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-medium text-white border-2 border-white/30 hover:border-white/65 hover:bg-white/10 transition-all duration-200"
              >
                <Icon name="PhoneIcon" size={16} />
                Talk to Sales
              </Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
